import React, { useState, useEffect } from 'react';
import { CreditCard, MessageSquare, List, LogOut, Send, UserPlus } from 'lucide-react';

const Customers = ({ language }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Use the backend proxy URL
  const API_URL = 'http://localhost:5000/api';

  const translations = {
    en: {
      loginTitle: 'Customer Login',
      registerTitle: 'Register',
      username: 'Username',
      email: 'Email',
      password: 'Password',
      login: 'Log In',
      register: 'Register',
      switchToRegister: 'Need an account? Register',
      switchToLogin: 'Already have an account? Log In',
      planTitle: 'Subscription Plan',
      currentPlan: 'Current Plan',
      planName: 'Professional',
      nextPayment: 'Next Payment',
      paymentDate: 'August 1, 2024',
      feedbackTitle: 'Feedback & Support',
      feedbackPlaceholder: 'Report a bug or make a suggestion...',
      submitFeedback: 'Submit',
      tasksTitle: 'Your Tasks',
      noTasks: 'No tasks found.',
      logout: 'Logout',
      loading: 'Loading...',
      errorLogin: 'Login failed. Please check your credentials.',
      errorRegister: 'Registration failed. Please try again.',
      errorFetch: 'Failed to fetch data.',
      taskCreated: 'Feedback submitted successfully!',
      noProject: 'No project found to create task.',
      registerSuccess: 'Registration successful! Please log in.'
    },
    es: {
      loginTitle: 'Acceso Clientes',
      registerTitle: 'Registrarse',
      username: 'Usuario',
      email: 'Email',
      password: 'Contraseña',
      login: 'Iniciar Sesión',
      register: 'Registrarse',
      switchToRegister: '¿Necesitas una cuenta? Regístrate',
      switchToLogin: '¿Ya tienes cuenta? Inicia Sesión',
      planTitle: 'Plan de Suscripción',
      currentPlan: 'Plan Actual',
      planName: 'Profesional',
      nextPayment: 'Próximo Pago',
      paymentDate: '1 de Agosto, 2024',
      feedbackTitle: 'Comentarios y Soporte',
      feedbackPlaceholder: 'Reportar un error o hacer una sugerencia...',
      submitFeedback: 'Enviar',
      tasksTitle: 'Tus Tareas',
      noTasks: 'No se encontraron tareas.',
      logout: 'Cerrar Sesión',
      loading: 'Cargando...',
      errorLogin: 'Fallo al iniciar sesión. Verifique sus credenciales.',
      errorRegister: 'Registro fallido. Intente nuevamente.',
      errorFetch: 'Fallo al obtener datos.',
      taskCreated: '¡Comentarios enviados con éxito!',
      noProject: 'No se encontró proyecto para crear la tarea.',
      registerSuccess: '¡Registro exitoso! Por favor inicia sesión.'
    },
    sv: {
      loginTitle: 'Kundinloggning',
      registerTitle: 'Registrera',
      username: 'Användarnamn',
      email: 'E-post',
      password: 'Lösenord',
      login: 'Logga In',
      register: 'Registrera',
      switchToRegister: 'Behöver du ett konto? Registrera dig',
      switchToLogin: 'Har du redan ett konto? Logga in',
      planTitle: 'Prenumerationsplan',
      currentPlan: 'Nuvarande Plan',
      planName: 'Professionell',
      nextPayment: 'Nästa Betalning',
      paymentDate: '1 Augusti, 2024',
      feedbackTitle: 'Feedback & Support',
      feedbackPlaceholder: 'Rapportera en bugg eller ge förslag...',
      submitFeedback: 'Skicka',
      tasksTitle: 'Dina Uppgifter',
      noTasks: 'Inga uppgifter hittades.',
      logout: 'Logga Ut',
      loading: 'Laddar...',
      errorLogin: 'Inloggning misslyckades. Kontrollera dina uppgifter.',
      errorRegister: 'Registrering misslyckades. Försök igen.',
      errorFetch: 'Misslyckades med att hämta data.',
      taskCreated: 'Feedback skickad!',
      noProject: 'Inget projekt hittades för att skapa uppgift.',
      registerSuccess: 'Registrering lyckades! Vänligen logga in.'
    }
  };

  const t = translations[language] || translations['en'];

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      if (!response.ok) throw new Error('Login failed');

      const data = await response.json();
      const jwtToken = data.token;
      setToken(jwtToken);
      setIsAuthenticated(true);
      
      await fetchProjects(jwtToken);
      await fetchTasks(jwtToken);
      
    } catch (err) {
      console.error(err);
      setError(t.errorLogin);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password })
      });

      if (!response.ok) throw new Error('Registration failed');

      alert(t.registerSuccess);
      setIsRegistering(false);
      setPassword(''); // Clear password for security
    } catch (err) {
      console.error(err);
      setError(t.errorRegister);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async (jwtToken) => {
    try {
      const response = await fetch(`${API_URL}/projects`, {
        headers: { 'Authorization': `Bearer ${jwtToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (e) {
      console.error("Error fetching projects", e);
    }
  };

  const fetchTasks = async (jwtToken) => {
    try {
      const response = await fetch(`${API_URL}/tasks`, {
        headers: { 'Authorization': `Bearer ${jwtToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTasks(data || []);
      }
    } catch (e) {
      console.error("Error fetching tasks", e);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;
    
    if (projects.length === 0) {
        setError(t.noProject);
        return;
    }
    
    const projectId = projects[0].id;

    try {
      const response = await fetch(`${API_URL}/projects/${projectId}/tasks`, {
        method: 'POST',
        headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ title: newTaskTitle })
      });

      if (response.ok) {
        setNewTaskTitle('');
        alert(t.taskCreated);
        fetchTasks(token);
      } else {
          throw new Error('Failed to create task');
      }
    } catch (err) {
      console.error(err);
      setError(t.errorFetch);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="container" style={{ maxWidth: '400px', marginTop: '2rem' }}>
        <div className="pricing-card">
          <h2>{isRegistering ? t.registerTitle : t.loginTitle}</h2>
          <form onSubmit={isRegistering ? handleRegister : handleLogin}>
            <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
              <label>{t.username}</label>
              <input 
                type="text" 
                value={username} 
                onChange={(e) => setUsername(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
                required
              />
            </div>
            {isRegistering && (
              <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
                <label>{t.email}</label>
                <input 
                  type="email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
                  required
                />
              </div>
            )}
            <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
              <label>{t.password}</label>
              <input 
                type="password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
                required
              />
            </div>
            {error && <p style={{ color: 'red' }}>{error}</p>}
            <button type="submit" className="cta-button" disabled={loading} style={{ width: '100%' }}>
              {loading ? t.loading : (isRegistering ? t.register : t.login)}
            </button>
          </form>
          <div style={{ marginTop: '1rem' }}>
            <button 
              onClick={() => {
                setIsRegistering(!isRegistering);
                setError('');
                setPassword('');
              }} 
              style={{ background: 'none', border: 'none', color: '#667eea', cursor: 'pointer', textDecoration: 'underline' }}
            >
              {isRegistering ? t.switchToLogin : t.switchToRegister}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', padding: '0 1rem' }}>
        <h1 style={{ color: 'white' }}>{t.loginTitle}</h1>
        <button onClick={() => setIsAuthenticated(false)} className="cta-button" style={{ background: '#e53e3e', width: 'auto' }}>
          <LogOut size={16} style={{ marginRight: '0.5rem', display: 'inline' }} /> {t.logout}
        </button>
      </div>

      <div className="pricing-cards">
        {/* Plan Card */}
        <div className="pricing-card">
          <CreditCard size={32} style={{ color: '#667eea', marginBottom: '1rem' }} />
          <h3>{t.planTitle}</h3>
          <div style={{ textAlign: 'left', marginTop: '1rem' }}>
            <p><strong>{t.currentPlan}:</strong> {t.planName}</p>
            <p><strong>{t.nextPayment}:</strong> {t.paymentDate}</p>
          </div>
        </div>

        {/* Feedback Card */}
        <div className="pricing-card">
          <MessageSquare size={32} style={{ color: '#667eea', marginBottom: '1rem' }} />
          <h3>{t.feedbackTitle}</h3>
          <form onSubmit={handleCreateTask} style={{ marginTop: '1rem' }}>
            <textarea
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              placeholder={t.feedbackPlaceholder}
              style={{ width: '100%', padding: '0.5rem', minHeight: '100px', marginBottom: '1rem' }}
              required
            />
            <button type="submit" className="cta-button" style={{ width: '100%' }}>
              <Send size={16} style={{ marginRight: '0.5rem', display: 'inline' }} /> {t.submitFeedback}
            </button>
          </form>
        </div>

        {/* Tasks Card */}
        <div className="pricing-card">
          <List size={32} style={{ color: '#667eea', marginBottom: '1rem' }} />
          <h3>{t.tasksTitle}</h3>
          <div style={{ textAlign: 'left', marginTop: '1rem', maxHeight: '200px', overflowY: 'auto' }}>
            {tasks.length === 0 ? (
              <p>{t.noTasks}</p>
            ) : (
              <ul style={{ paddingLeft: '1.2rem' }}>
                {tasks.map((task) => (
                  <li key={task.id} style={{ marginBottom: '0.5rem' }}>
                    {task.title}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Customers;